import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import TopicCard from "@/components/feature/TopicCard";
import CategorySidebar from "./components/CategorySidebar";
import { categories } from "@/mocks/categories";
import { topics } from "@/mocks/topics";
import { usePageMeta } from "@/hooks/usePageMeta";

type SortKey = "latest" | "hot" | "discussed";

const sortTabs: { key: SortKey; label: string; icon: string }[] = [
  { key: "latest", label: "最新话题", icon: "ri-time-line" },
  { key: "hot", label: "热度最高", icon: "ri-fire-line" },
  { key: "discussed", label: "讨论最多", icon: "ri-chat-3-line" },
];

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const [sort, setSort] = useState<SortKey>("latest");

  const category = categories.find((item) => item.slug === slug);
  const categoryName = category?.name ?? "话题分类";

  const list = useMemo(() => {
    const base = topics.filter((topic) => topic.category === slug);
    if (sort === "hot") return [...base].sort((a, b) => b.heat - a.heat);
    if (sort === "discussed") return [...base].sort((a, b) => b.comments - a.comments);
    return [...base].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
  }, [slug, sort]);

  usePageMeta({
    title: `${categoryName}话题讨论 | 黑瓜吃料瓜田讨论区`,
    description: `${categoryName}分类下的热门瓜田话题合集。${category?.desc ?? "汇聚围观群众观点，实时讨论当下最热话题。"}来黑瓜吃料讨论区连线吃瓜，边吃边聊不落单。`,
    keywords: `黑瓜吃料,${categoryName},瓜田讨论区,连线吃瓜,围观群众热议`,
    path: `/category/${slug}`,
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${categoryName}话题讨论`,
        url: `https://geofix.cn/category/${slug}`,
        inLanguage: "zh-CN",
        description: category?.desc ?? "黑瓜吃料话题分类页",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://geofix.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: categoryName,
            item: `https://geofix.cn/category/${slug}`,
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: list.map((topic, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: topic.title,
          url: `https://geofix.cn/topic/${topic.id}`,
        })),
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100 py-8 md:py-10">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6">
            <Breadcrumbs items={[{ label: "话题分类" }, { label: categoryName }]} />
            <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
              <div className="max-w-2xl">
                <span className="flex items-center gap-2 text-xs font-medium tracking-wide text-accent-700">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className={`${category?.icon ?? "ri-layout-grid-line"} text-base`} />
                  </span>
                  话题分类
                </span>
                <h1 className="mt-2 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
                  {categoryName}
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  {category?.desc ?? "这个分类暂时没有找到，换个分类看看其他瓜田吧。"}
                </p>
              </div>
              <div className="flex items-center gap-3 rounded-card border border-background-200 bg-background-50 px-5 py-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-chat-smile-2-line text-2xl" />
                </span>
                <div>
                  <p className="font-heading text-2xl font-bold text-foreground-950">{list.length}</p>
                  <p className="text-xs text-foreground-500">个话题正在讨论</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14">
          <div className="mx-auto grid max-w-[1280px] grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                  {sortTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setSort(tab.key)}
                      className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                        sort === tab.key
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:text-foreground-950"
                      }`}
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className={`${tab.icon} text-sm`} />
                      </span>
                      {tab.label}
                    </button>
                  ))}
                </div>
                <span className="text-xs text-foreground-500">共 {list.length} 个话题</span>
              </div>

              {list.length > 0 ? (
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {list.map((topic) => (
                    <TopicCard key={topic.id} topic={topic} />
                  ))}
                </div>
              ) : (
                <div className="mt-6 flex flex-col items-center justify-center rounded-card border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                    <i className="ri-emotion-sad-line text-3xl" />
                  </span>
                  <p className="mt-4 text-base font-medium text-foreground-900">该分类还没有话题</p>
                  <p className="mt-1.5 text-sm text-foreground-600">
                    换个分类逛逛，或者回首页看看今日热瓜。
                  </p>
                  <Link
                    to="/"
                    className="mt-5 flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    返回首页
                  </Link>
                </div>
              )}
            </div>

            <div className="lg:col-span-4">
              <CategorySidebar activeSlug={slug} />
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}